import { useState, useMemo } from "react";
import {
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area, FunnelChart, Funnel,
  LabelList, Cell, ReferenceLine
} from "recharts";

// ─── THEME ───────────────────────────────────────────────────────────────────
const T = {
  bg: "#0A0C10",
  surface: "#111318",
  surfaceHigh: "#181C24",
  border: "#1E2330",
  accent: "#00E5A0",
  accentDim: "#00E5A020",
  accentMid: "#00E5A060",
  blue: "#4B9FFF",
  blueDim: "#4B9FFF20",
  orange: "#FF8C42",
  orangeDim: "#FF8C4220",
  red: "#FF4D6A",
  redDim: "#FF4D6A20",
  yellow: "#FFD166",
  purple: "#9B5DE5",
  text: "#E8EAF0",
  textMid: "#8B92A8",
  textDim: "#4A5068",
  font: "'DM Mono', monospace",
  fontDisplay: "'Syne', sans-serif",
};

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const days30 = Array.from({length:30},(_,i)=>{
  const d = new Date(2025,3,i+1);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
});

function rand(min,max){ return Math.round(Math.random()*(max-min)+min); }
function randF(min,max,dp=2){ return parseFloat((Math.random()*(max-min)+min).toFixed(dp)); }

// Store performance daily
const storeData = days30.map((date,i)=>({
  date,
  visitors: rand(1800,3200) + i*18,
  gmv: rand(18000000,32000000) + i*200000,
  orders: rand(120,280) + i*2,
  adSpend: rand(2500000,5000000),
  adsRevenue: rand(8000000,18000000),
  impressions: rand(40000,90000),
  clicks: rand(2000,5500),
}));

storeData.forEach(d=>{
  d.cvr = parseFloat((d.orders/d.visitors*100).toFixed(2));
  d.aov = Math.round(d.gmv/d.orders);
  d.roas = parseFloat((d.adsRevenue/d.adSpend).toFixed(2));
  d.ctr = parseFloat((d.clicks/d.impressions*100).toFixed(2));
  d.organicRevenue = d.gmv - d.adsRevenue;
  d.adsContrib = parseFloat((d.adsRevenue/d.gmv*100).toFixed(1));
  d.blendedROAS = parseFloat((d.gmv/d.adSpend).toFixed(2));
  d.cvr_ads = parseFloat((d.orders/d.clicks*100).toFixed(2));
});

// Products
const PRODUCTS = [
  { id:1, name:"Vitamin C Serum 30ml", sku:"VCS-030" },
  { id:2, name:"Retinol Night Cream", sku:"RNC-050" },
  { id:3, name:"Hyaluronic Acid Toner", sku:"HAT-200" },
  { id:4, name:"SPF 50 Sunscreen", sku:"SUN-050" },
  { id:5, name:"Niacinamide Serum", sku:"NIA-030" },
  { id:6, name:"Collagen Face Mask", sku:"CFM-010" },
  { id:7, name:"Micellar Cleansing Water", sku:"MCW-250" },
  { id:8, name:"Brightening Eye Cream", sku:"BEC-015" },
];

const productData = PRODUCTS.map((p,i)=>{
  const adSpend = rand(800000, 4500000);
  const adsRevenue = adSpend * randF(1.8,6.2);
  const impressions = rand(8000,55000);
  const clicks = Math.round(impressions * randF(0.04,0.12));
  const orders = Math.round(clicks * randF(0.04,0.18));
  const totalRevenue = adsRevenue * randF(1.1, 2.2);
  const visitors = rand(400,2800);
  return {
    ...p,
    adSpend: Math.round(adSpend),
    adsRevenue: Math.round(adsRevenue),
    roas: parseFloat((adsRevenue/adSpend).toFixed(2)),
    impressions, clicks, orders,
    ctr: parseFloat((clicks/impressions*100).toFixed(2)),
    cvr: parseFloat((orders/clicks*100).toFixed(2)),
    totalRevenue: Math.round(totalRevenue),
    visitors,
    trafficShare: 0, salesShare: 0,
  };
});

const totalTraffic = productData.reduce((s,p)=>s+p.visitors,0);
const totalProdSales = productData.reduce((s,p)=>s+p.totalRevenue,0);
productData.forEach(p=>{
  p.trafficShare = parseFloat((p.visitors/totalTraffic*100).toFixed(1));
  p.salesShare = parseFloat((p.totalRevenue/totalProdSales*100).toFixed(1));
  p.efficiencyScore = parseFloat(((p.roas*0.4 + p.cvr*0.3 + p.ctr*0.3)).toFixed(2));
});

// Product daily detail
const productDetailData = days30.map((date,i)=>{
  const base = { date };
  PRODUCTS.slice(0,4).forEach(p=>{
    const spend = rand(80000,300000);
    const rev = spend * randF(2.0,5.5);
    base[`${p.id}_spend`] = Math.round(spend);
    base[`${p.id}_rev`] = Math.round(rev);
    base[`${p.id}_roas`] = parseFloat((rev/spend).toFixed(2));
  });
  return base;
});

// ─── UTILITIES ───────────────────────────────────────────────────────────────
const fmt = {
  idr: v => `Rp ${(v/1000000).toFixed(1)}M`,
  idrFull: v => `Rp ${v.toLocaleString()}`,
  pct: v => `${v}%`,
  num: v => v?.toLocaleString(),
  roas: v => `${v}x`,
};

function delta(curr, prev){
  if(!prev) return null;
  const pct = ((curr-prev)/prev*100).toFixed(1);
  return { pct: parseFloat(pct), up: parseFloat(pct)>0 };
}

// Summary KPIs
const totalGMV = storeData.reduce((s,d)=>s+d.gmv,0);
const totalOrders = storeData.reduce((s,d)=>s+d.orders,0);
const totalVisitors = storeData.reduce((s,d)=>s+d.visitors,0);
const totalAdSpend = storeData.reduce((s,d)=>s+d.adSpend,0);
const totalAdsRev = storeData.reduce((s,d)=>s+d.adsRevenue,0);
const totalImpressions = storeData.reduce((s,d)=>s+d.impressions,0);
const totalClicks = storeData.reduce((s,d)=>s+d.clicks,0);
const avgCVR = (totalOrders/totalVisitors*100).toFixed(2);
const avgAOV = Math.round(totalGMV/totalOrders);
const avgROAS = (totalAdsRev/totalAdSpend).toFixed(2);
const avgCTR = (totalClicks/totalImpressions*100).toFixed(2);
const avgBlendedROAS = (totalGMV/totalAdSpend).toFixed(2);
const adsContribAvg = (totalAdsRev/totalGMV*100).toFixed(1);

// Scaling framework categorization
function categorize(p){
  if(p.roas >= 4 && p.cvr >= 8) return "SCALE";
  if(p.roas >= 3 && p.cvr < 8) return "OPPORTUNITY";
  if(p.roas >= 2 && p.roas < 3) return "OPTIMIZE";
  return "KILL";
}
const scalingColors = { SCALE: T.accent, OPPORTUNITY: T.blue, OPTIMIZE: T.yellow, KILL: T.red };
const scalingBg = { SCALE: T.accentDim, OPPORTUNITY: T.blueDim, OPTIMIZE: "#FFD16620", KILL: T.redDim };

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

function KPICard({ label, value, sub, trend, color=T.accent, icon }){
  const up = trend?.up;
  return (
    <div style={{
      background: T.surface,
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      padding: "18px 20px",
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{
        position:"absolute", top:0, left:0, right:0, height:2,
        background: `linear-gradient(90deg, ${color}, transparent)`
      }}/>
      <div style={{ color: T.textMid, fontSize:11, fontFamily:T.font, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6 }}>{label}</div>
      <div style={{ color: T.text, fontSize:22, fontFamily:T.fontDisplay, fontWeight:700, lineHeight:1.2 }}>{value}</div>
      {sub && <div style={{ color: T.textDim, fontSize:11, fontFamily:T.font, marginTop:4 }}>{sub}</div>}
      {trend && (
        <div style={{
          marginTop:8,
          display:"inline-flex", alignItems:"center", gap:4,
          background: up ? T.accentDim : T.redDim,
          color: up ? T.accent : T.red,
          borderRadius:20, padding:"2px 8px", fontSize:11, fontFamily:T.font
        }}>
          {up ? "↑" : "↓"} {Math.abs(trend.pct)}%
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, subtitle, badge }){
  return (
    <div style={{ marginBottom:24 }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:4 }}>
        <div style={{ width:3, height:20, background:T.accent, borderRadius:2 }}/>
        <h2 style={{ color:T.text, fontSize:18, fontFamily:T.fontDisplay, fontWeight:700, margin:0 }}>{title}</h2>
        {badge && (
          <span style={{
            background:T.accentDim, color:T.accent,
            fontSize:10, fontFamily:T.font, letterSpacing:"0.08em",
            padding:"3px 8px", borderRadius:20, border:`1px solid ${T.accentMid}`
          }}>{badge}</span>
        )}
      </div>
      {subtitle && <p style={{ color:T.textMid, fontSize:12, fontFamily:T.font, margin:"0 0 0 15px", lineHeight:1.6 }}>{subtitle}</p>}
    </div>
  );
}

function ChartCard({ title, children, span=1 }){
  return (
    <div style={{
      background: T.surface,
      border: `1px solid ${T.border}`,
      borderRadius:12, padding:"20px",
      gridColumn: span > 1 ? `span ${span}` : undefined,
    }}>
      {title && <div style={{ color:T.textMid, fontSize:11, fontFamily:T.font, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:16 }}>{title}</div>}
      {children}
    </div>
  );
}

const CHART_STYLE = {
  fontSize:10, fontFamily:T.font,
};

const tooltipStyle = {
  contentStyle:{ background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:8, fontSize:11, fontFamily:T.font },
  labelStyle:{ color:T.textMid },
  itemStyle:{ color:T.text },
};

// ─── SECTIONS ────────────────────────────────────────────────────────────────

function Section1(){
  const funnelData = [
    { name:"Visitors", value: totalVisitors, fill: T.blue },
    { name:"Sessions w/ Product View", value: Math.round(totalVisitors*0.62), fill: T.purple },
    { name:"Add to Cart", value: Math.round(totalVisitors*0.18), fill: T.yellow },
    { name:"Orders", value: totalOrders, fill: T.accent },
  ];
  return (
    <div>
      <SectionHeader title="Store Performance" subtitle="Overall business health — traffic, sales, and conversion trends." badge="BUSINESS HEALTH"/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:24 }}>
        <KPICard label="Total GMV" value={fmt.idr(totalGMV)} sub="30-day period" trend={{pct:12.4,up:true}} color={T.accent}/>
        <KPICard label="Total Visitors" value={fmt.num(totalVisitors)} sub="store traffic" trend={{pct:8.7,up:true}} color={T.blue}/>
        <KPICard label="Total Orders" value={fmt.num(totalOrders)} trend={{pct:15.2,up:true}} color={T.purple}/>
        <KPICard label="Avg Conversion" value={`${avgCVR}%`} sub="visitors → orders" trend={{pct:0.3,up:false}} color={T.yellow}/>
        <KPICard label="Avg AOV" value={fmt.idr(avgAOV)} sub="per order" trend={{pct:3.1,up:true}} color={T.orange}/>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16, marginBottom:16 }}>
        <ChartCard title="GMV Daily Trend (Rp)">
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={storeData} {...CHART_STYLE}>
              <defs>
                <linearGradient id="gmvGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={T.accent} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={T.accent} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="date" tick={{fill:T.textDim,...CHART_STYLE}} interval={6}/>
              <YAxis tickFormatter={v=>`${(v/1000000).toFixed(0)}M`} tick={{fill:T.textDim,...CHART_STYLE}}/>
              <Tooltip {...tooltipStyle} formatter={v=>[fmt.idr(v),"GMV"]}/>
              <Area type="monotone" dataKey="gmv" stroke={T.accent} fill="url(#gmvGrad)" strokeWidth={2}/>
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Store Conversion Rate">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={storeData} {...CHART_STYLE}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="date" tick={{fill:T.textDim,...CHART_STYLE}} interval={6}/>
              <YAxis tick={{fill:T.textDim,...CHART_STYLE}} tickFormatter={v=>`${v}%`}/>
              <Tooltip {...tooltipStyle} formatter={v=>[`${v}%`,"CVR"]}/>
              <ReferenceLine y={parseFloat(avgCVR)} stroke={T.yellow} strokeDasharray="4 4" label={{value:"avg",fill:T.yellow,fontSize:9,fontFamily:T.font}}/>
              <Line type="monotone" dataKey="cvr" stroke={T.yellow} dot={false} strokeWidth={2}/>
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <ChartCard title="Traffic vs Orders">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={storeData} {...CHART_STYLE}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="date" tick={{fill:T.textDim,...CHART_STYLE}} interval={6}/>
              <YAxis yAxisId="l" tick={{fill:T.textDim,...CHART_STYLE}}/>
              <YAxis yAxisId="r" orientation="right" tick={{fill:T.textDim,...CHART_STYLE}}/>
              <Tooltip {...tooltipStyle}/>
              <Legend wrapperStyle={{fontSize:10,fontFamily:T.font}}/>
              <Line yAxisId="l" type="monotone" dataKey="visitors" stroke={T.blue} dot={false} strokeWidth={2} name="Visitors"/>
              <Line yAxisId="r" type="monotone" dataKey="orders" stroke={T.accent} dot={false} strokeWidth={2} name="Orders"/>
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="AOV Trend">
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={storeData} {...CHART_STYLE}>
              <defs>
                <linearGradient id="aovGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={T.orange} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={T.orange} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="date" tick={{fill:T.textDim,...CHART_STYLE}} interval={6}/>
              <YAxis tickFormatter={v=>`${(v/1000).toFixed(0)}K`} tick={{fill:T.textDim,...CHART_STYLE}}/>
              <Tooltip {...tooltipStyle} formatter={v=>[fmt.idr(v),"AOV"]}/>
              <Area type="monotone" dataKey="aov" stroke={T.orange} fill="url(#aovGrad)" strokeWidth={2}/>
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function Section2(){
  return (
    <div>
      <SectionHeader title="Ads Performance" subtitle="Advertising efficiency — spend, revenue, ROAS, and funnel quality." badge="ADS OVERVIEW"/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:24 }}>
        <KPICard label="Total Ad Spend" value={fmt.idr(totalAdSpend)} trend={{pct:18.3,up:true}} color={T.red}/>
        <KPICard label="Ads Revenue" value={fmt.idr(totalAdsRev)} trend={{pct:24.1,up:true}} color={T.accent}/>
        <KPICard label="Avg ROAS" value={`${avgROAS}x`} sub="ads revenue/spend" trend={{pct:4.8,up:true}} color={T.blue}/>
        <KPICard label="Avg CTR" value={`${avgCTR}%`} sub="click-through rate" trend={{pct:1.2,up:false}} color={T.yellow}/>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16, marginBottom:16 }}>
        <ChartCard title="Ad Spend vs Ads Revenue vs ROAS (Daily)">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={storeData} {...CHART_STYLE}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="date" tick={{fill:T.textDim,...CHART_STYLE}} interval={6}/>
              <YAxis yAxisId="l" tickFormatter={v=>`${(v/1000000).toFixed(0)}M`} tick={{fill:T.textDim,...CHART_STYLE}}/>
              <YAxis yAxisId="r" orientation="right" tick={{fill:T.textDim,...CHART_STYLE}} domain={[0,10]}/>
              <Tooltip {...tooltipStyle} formatter={(v,n)=>n==="ROAS"?[`${v}x`,n]:[fmt.idr(v),n]}/>
              <Legend wrapperStyle={{fontSize:10,fontFamily:T.font}}/>
              <Line yAxisId="l" dataKey="adSpend" stroke={T.red} dot={false} strokeWidth={2} name="Ad Spend"/>
              <Line yAxisId="l" dataKey="adsRevenue" stroke={T.accent} dot={false} strokeWidth={2} name="Ads Revenue"/>
              <Line yAxisId="r" dataKey="roas" stroke={T.blue} dot={false} strokeWidth={2} strokeDasharray="5 3" name="ROAS"/>
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Ads Funnel">
          <div style={{ padding:"8px 0" }}>
            {[
              { label:"Impressions", value: totalImpressions, pct:100, color:T.blue },
              { label:"Clicks", value: totalClicks, pct:(totalClicks/totalImpressions*100).toFixed(1), color:T.purple },
              { label:"Orders", value: totalOrders, pct:(totalOrders/totalClicks*100).toFixed(1), color:T.accent },
            ].map((s,i)=>(
              <div key={i} style={{ marginBottom:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                  <span style={{ color:T.textMid, fontSize:11, fontFamily:T.font }}>{s.label}</span>
                  <span style={{ color:T.text, fontSize:11, fontFamily:T.font }}>{fmt.num(s.value)}</span>
                </div>
                <div style={{ background:T.border, borderRadius:4, height:8, overflow:"hidden" }}>
                  <div style={{ width:`${s.pct}%`, height:"100%", background:s.color, borderRadius:4, maxWidth:"100%" }}/>
                </div>
                <div style={{ color:T.textDim, fontSize:10, fontFamily:T.font, marginTop:2 }}>{s.pct}% of impressions</div>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <ChartCard title="CTR Trend">
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={storeData} {...CHART_STYLE}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="date" tick={{fill:T.textDim,...CHART_STYLE}} interval={6}/>
              <YAxis tick={{fill:T.textDim,...CHART_STYLE}} tickFormatter={v=>`${v}%`}/>
              <Tooltip {...tooltipStyle} formatter={v=>[`${v}%`,"CTR"]}/>
              <Line type="monotone" dataKey="ctr" stroke={T.yellow} dot={false} strokeWidth={2}/>
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Ads CVR (Clicks → Orders)">
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={storeData} {...CHART_STYLE}>
              <defs>
                <linearGradient id="cvrGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={T.purple} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={T.purple} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="date" tick={{fill:T.textDim,...CHART_STYLE}} interval={6}/>
              <YAxis tick={{fill:T.textDim,...CHART_STYLE}} tickFormatter={v=>`${v}%`}/>
              <Tooltip {...tooltipStyle} formatter={v=>[`${v}%`,"Ads CVR"]}/>
              <Area type="monotone" dataKey="cvr_ads" stroke={T.purple} fill="url(#cvrGrad)" strokeWidth={2}/>
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function Section3(){
  const sorted_roas = [...productData].sort((a,b)=>b.roas-a.roas);
  const sorted_rev = [...productData].sort((a,b)=>b.adsRevenue-a.adsRevenue);
  return (
    <div>
      <SectionHeader title="Product Ads Performance" subtitle="Which products are winning in advertising — hero products, inefficiencies, scaling candidates." badge="PRODUCT ADS"/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
        <ChartCard title="Top Products by ROAS">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={sorted_roas} layout="vertical" {...CHART_STYLE}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis type="number" tick={{fill:T.textDim,...CHART_STYLE}} tickFormatter={v=>`${v}x`}/>
              <YAxis type="category" dataKey="name" tick={{fill:T.textMid,...CHART_STYLE}} width={130} style={{fontSize:10}}/>
              <Tooltip {...tooltipStyle} formatter={v=>[`${v}x`,"ROAS"]}/>
              <Bar dataKey="roas" radius={[0,4,4,0]}>
                {sorted_roas.map((p,i)=><Cell key={i} fill={p.roas>=4?T.accent:p.roas>=3?T.blue:p.roas>=2?T.yellow:T.red}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Top Products by Ads Revenue">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={sorted_rev} layout="vertical" {...CHART_STYLE}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis type="number" tick={{fill:T.textDim,...CHART_STYLE}} tickFormatter={v=>`${(v/1000000).toFixed(0)}M`}/>
              <YAxis type="category" dataKey="name" tick={{fill:T.textMid,...CHART_STYLE}} width={130} style={{fontSize:10}}/>
              <Tooltip {...tooltipStyle} formatter={v=>[fmt.idr(v),"Ads Revenue"]}/>
              <Bar dataKey="adsRevenue" fill={T.blue} radius={[0,4,4,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <ChartCard title="Product Ads Performance Table">
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11, fontFamily:T.font }}>
            <thead>
              <tr>
                {["Product","Ad Spend","Ads Revenue","ROAS","Impressions","Clicks","CTR","Orders","CVR","Category"].map(h=>(
                  <th key={h} style={{ padding:"8px 10px", textAlign:"left", color:T.textDim, borderBottom:`1px solid ${T.border}`, whiteSpace:"nowrap", fontWeight:500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {productData.map((p,i)=>{
                const cat = categorize(p);
                return (
                  <tr key={i} style={{ borderBottom:`1px solid ${T.border}20` }}>
                    <td style={{ padding:"10px", color:T.text }}>{p.name}</td>
                    <td style={{ padding:"10px", color:T.red }}>{fmt.idr(p.adSpend)}</td>
                    <td style={{ padding:"10px", color:T.accent }}>{fmt.idr(p.adsRevenue)}</td>
                    <td style={{ padding:"10px", color:p.roas>=4?T.accent:p.roas>=3?T.blue:p.roas>=2?T.yellow:T.red, fontWeight:700 }}>{p.roas}x</td>
                    <td style={{ padding:"10px", color:T.textMid }}>{fmt.num(p.impressions)}</td>
                    <td style={{ padding:"10px", color:T.textMid }}>{fmt.num(p.clicks)}</td>
                    <td style={{ padding:"10px", color:T.yellow }}>{p.ctr}%</td>
                    <td style={{ padding:"10px", color:T.textMid }}>{p.orders}</td>
                    <td style={{ padding:"10px", color:p.cvr>=8?T.accent:p.cvr>=4?T.blue:T.red }}>{p.cvr}%</td>
                    <td style={{ padding:"10px" }}>
                      <span style={{ background:scalingBg[cat], color:scalingColors[cat], padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700 }}>{cat}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}

function Section4(){
  const [selectedProduct, setSelectedProduct] = useState(PRODUCTS[0].id);
  const sp = PRODUCTS.find(p=>p.id===selectedProduct);
  const chartData = productDetailData.map(d=>({
    date: d.date,
    spend: d[`${selectedProduct}_spend`],
    revenue: d[`${selectedProduct}_rev`],
    roas: d[`${selectedProduct}_roas`],
  }));
  return (
    <div>
      <SectionHeader title="Product Detail Performance" subtitle="Daily trend analysis per product — detect ROAS drops, scaling effects, and stability." badge="PRODUCT DETAIL"/>
      <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
        {PRODUCTS.slice(0,4).map(p=>(
          <button key={p.id} onClick={()=>setSelectedProduct(p.id)} style={{
            background: selectedProduct===p.id ? T.accentDim : T.surface,
            border: `1px solid ${selectedProduct===p.id ? T.accent : T.border}`,
            color: selectedProduct===p.id ? T.accent : T.textMid,
            padding:"6px 14px", borderRadius:20, fontSize:11, fontFamily:T.font,
            cursor:"pointer", transition:"all 0.2s"
          }}>{p.name}</button>
        ))}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16, marginBottom:16 }}>
        <ChartCard title={`Daily Spend vs Revenue — ${sp.name}`}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData} {...CHART_STYLE}>
              <defs>
                <linearGradient id="revGrad2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={T.accent} stopOpacity={0.2}/>
                  <stop offset="95%" stopColor={T.accent} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="spendGrad2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={T.red} stopOpacity={0.2}/>
                  <stop offset="95%" stopColor={T.red} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="date" tick={{fill:T.textDim,...CHART_STYLE}} interval={6}/>
              <YAxis tickFormatter={v=>`${(v/1000).toFixed(0)}K`} tick={{fill:T.textDim,...CHART_STYLE}}/>
              <Tooltip {...tooltipStyle} formatter={v=>[fmt.idr(v)]}/>
              <Legend wrapperStyle={{fontSize:10,fontFamily:T.font}}/>
              <Area type="monotone" dataKey="revenue" stroke={T.accent} fill="url(#revGrad2)" strokeWidth={2} name="Ads Revenue"/>
              <Area type="monotone" dataKey="spend" stroke={T.red} fill="url(#spendGrad2)" strokeWidth={2} name="Ad Spend"/>
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Daily ROAS">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} {...CHART_STYLE}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="date" tick={{fill:T.textDim,...CHART_STYLE}} interval={6}/>
              <YAxis tick={{fill:T.textDim,...CHART_STYLE}} tickFormatter={v=>`${v}x`} domain={[0,"auto"]}/>
              <Tooltip {...tooltipStyle} formatter={v=>[`${v}x`,"ROAS"]}/>
              <ReferenceLine y={3} stroke={T.yellow} strokeDasharray="4 4" label={{value:"target",fill:T.yellow,fontSize:9}}/>
              <Line type="monotone" dataKey="roas" stroke={T.blue} dot={false} strokeWidth={2.5}/>
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function Section5(){
  const scatter = productData.map(p=>({ name:p.name, x:p.trafficShare, y:p.salesShare, z:p.totalRevenue }));
  return (
    <div>
      <SectionHeader title="Product Contribution Analysis" subtitle="Which products drive store traffic and sales — hero products, hidden gems, conversion killers." badge="CONTRIBUTION"/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
        <ChartCard title="Traffic Contribution by Product (%)">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={[...productData].sort((a,b)=>b.trafficShare-a.trafficShare)} {...CHART_STYLE}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="name" tick={{fill:T.textDim,...CHART_STYLE}} angle={-20} interval={0} height={50} style={{fontSize:8}}/>
              <YAxis tick={{fill:T.textDim,...CHART_STYLE}} tickFormatter={v=>`${v}%`}/>
              <Tooltip {...tooltipStyle} formatter={v=>[`${v}%`,"Traffic Share"]}/>
              <Bar dataKey="trafficShare" fill={T.blue} radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Sales Contribution by Product (%)">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={[...productData].sort((a,b)=>b.salesShare-a.salesShare)} {...CHART_STYLE}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="name" tick={{fill:T.textDim,...CHART_STYLE}} angle={-20} interval={0} height={50} style={{fontSize:8}}/>
              <YAxis tick={{fill:T.textDim,...CHART_STYLE}} tickFormatter={v=>`${v}%`}/>
              <Tooltip {...tooltipStyle} formatter={v=>[`${v}%`,"Sales Share"]}/>
              <Bar dataKey="salesShare" fill={T.accent} radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <ChartCard title="Traffic vs Sales Scatter — Identify Misalignment">
        <div style={{ marginBottom:8, fontSize:10, fontFamily:T.font, color:T.textMid }}>
          Points above diagonal = converts above traffic share (hidden gems). Points below = traffic without sales (conversion problem).
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <ScatterChart {...CHART_STYLE}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
            <XAxis dataKey="x" name="Traffic Share" tickFormatter={v=>`${v}%`} tick={{fill:T.textDim,...CHART_STYLE}} label={{value:"Traffic %",position:"insideBottom",offset:-5,fill:T.textDim,fontSize:10}}/>
            <YAxis dataKey="y" name="Sales Share" tickFormatter={v=>`${v}%`} tick={{fill:T.textDim,...CHART_STYLE}} label={{value:"Sales %",angle:-90,position:"insideLeft",fill:T.textDim,fontSize:10}}/>
            <Tooltip {...tooltipStyle} cursor={{strokeDasharray:"3 3"}}
              content={({payload})=>{
                if(!payload?.length) return null;
                const d = payload[0].payload;
                return <div style={{...tooltipStyle.contentStyle, padding:"10px"}}><div style={{color:T.text,fontSize:11}}>{d.name}</div><div style={{color:T.textMid,fontSize:10}}>Traffic: {d.x}% | Sales: {d.y}%</div></div>;
              }}
            />
            <Scatter data={scatter} fill={T.blue} opacity={0.8}/>
          </ScatterChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function Section6(){
  return (
    <div>
      <SectionHeader title="Ads Impact on Total Business" subtitle="Is advertising driving real growth? Organic vs paid sales, ads dependency, blended ROAS." badge="ADS IMPACT"/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:24 }}>
        <KPICard label="Total Sales" value={fmt.idr(totalGMV)} color={T.accent}/>
        <KPICard label="Organic Sales" value={fmt.idr(totalGMV-totalAdsRev)} sub="total − ads" color={T.blue}/>
        <KPICard label="Ads Contribution" value={`${adsContribAvg}%`} sub="ads/total sales" trend={{pct:2.1,up:true}} color={T.orange}/>
        <KPICard label="Blended ROAS" value={`${avgBlendedROAS}x`} sub="total sales/spend" color={T.purple}/>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16, marginBottom:16 }}>
        <ChartCard title="Ads Sales vs Organic Sales vs Total — Daily Stacked">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={storeData} {...CHART_STYLE}>
              <defs>
                <linearGradient id="orgGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={T.blue} stopOpacity={0.4}/>
                  <stop offset="95%" stopColor={T.blue} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="adsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={T.accent} stopOpacity={0.4}/>
                  <stop offset="95%" stopColor={T.accent} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="date" tick={{fill:T.textDim,...CHART_STYLE}} interval={6}/>
              <YAxis tickFormatter={v=>`${(v/1000000).toFixed(0)}M`} tick={{fill:T.textDim,...CHART_STYLE}}/>
              <Tooltip {...tooltipStyle} formatter={v=>[fmt.idr(v)]}/>
              <Legend wrapperStyle={{fontSize:10,fontFamily:T.font}}/>
              <Area type="monotone" dataKey="organicRevenue" stroke={T.blue} fill="url(#orgGrad)" strokeWidth={2} name="Organic Sales" stackId="a"/>
              <Area type="monotone" dataKey="adsRevenue" stroke={T.accent} fill="url(#adsGrad)" strokeWidth={2} name="Ads Sales" stackId="a"/>
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Ads Contribution % Trend">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={storeData} {...CHART_STYLE}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="date" tick={{fill:T.textDim,...CHART_STYLE}} interval={6}/>
              <YAxis tick={{fill:T.textDim,...CHART_STYLE}} tickFormatter={v=>`${v}%`}/>
              <Tooltip {...tooltipStyle} formatter={v=>[`${v}%`,"Ads Contribution"]}/>
              <ReferenceLine y={60} stroke={T.red} strokeDasharray="4 4" label={{value:"risk zone",fill:T.red,fontSize:9}}/>
              <Line type="monotone" dataKey="adsContrib" stroke={T.orange} dot={false} strokeWidth={2}/>
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <ChartCard title="Ad Spend vs Total GMV — Scaling Correlation">
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={storeData} {...CHART_STYLE}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
            <XAxis dataKey="date" tick={{fill:T.textDim,...CHART_STYLE}} interval={6}/>
            <YAxis yAxisId="l" tickFormatter={v=>`${(v/1000000).toFixed(0)}M`} tick={{fill:T.textDim,...CHART_STYLE}}/>
            <YAxis yAxisId="r" orientation="right" tickFormatter={v=>`${(v/1000000).toFixed(0)}M`} tick={{fill:T.textDim,...CHART_STYLE}}/>
            <Tooltip {...tooltipStyle} formatter={v=>[fmt.idr(v)]}/>
            <Legend wrapperStyle={{fontSize:10,fontFamily:T.font}}/>
            <Line yAxisId="l" type="monotone" dataKey="adSpend" stroke={T.red} dot={false} strokeWidth={2} name="Ad Spend"/>
            <Line yAxisId="r" type="monotone" dataKey="gmv" stroke={T.accent} dot={false} strokeWidth={2} name="Total GMV"/>
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function SectionScaling(){
  const categories = ["SCALE","OPPORTUNITY","OPTIMIZE","KILL"];
  const descriptions = {
    SCALE: "High ROAS (≥4x) + High CVR (≥8%). These are your proven winners. Increase budget aggressively — the unit economics support it.",
    OPPORTUNITY: "Good ROAS (≥3x) but low CVR (<8%). Traffic is interested but not converting. Fix landing page, pricing, or creative — then scale.",
    OPTIMIZE: "Marginal ROAS (2–3x). Breaking even or slightly profitable. Audit targeting, creative, and bid strategy before increasing spend.",
    KILL: "ROAS below 2x. Ads are destroying margin. Pause immediately or restructure campaign from scratch.",
  };
  return (
    <div>
      <SectionHeader title="Product Scaling Framework" subtitle="4-quadrant decision engine: know exactly which products to scale, fix, or kill." badge="FRAMEWORK"/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:24 }}>
        {categories.map(cat=>{
          const prods = productData.filter(p=>categorize(p)===cat);
          return (
            <div key={cat} style={{
              background: T.surface,
              border: `1px solid ${scalingColors[cat]}40`,
              borderRadius:12, padding:16,
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:scalingColors[cat] }}/>
                <span style={{ color:scalingColors[cat], fontFamily:T.fontDisplay, fontWeight:700, fontSize:14 }}>{cat}</span>
                <span style={{ marginLeft:"auto", background:scalingBg[cat], color:scalingColors[cat], fontSize:10, padding:"2px 8px", borderRadius:20, fontFamily:T.font }}>{prods.length} products</span>
              </div>
              <p style={{ color:T.textMid, fontSize:11, fontFamily:T.font, lineHeight:1.6, marginBottom:12 }}>{descriptions[cat]}</p>
              {prods.map(p=>(
                <div key={p.id} style={{ background:scalingBg[cat], borderRadius:6, padding:"6px 10px", marginBottom:6 }}>
                  <div style={{ color:T.text, fontSize:11, fontFamily:T.font }}>{p.name}</div>
                  <div style={{ color:scalingColors[cat], fontSize:10, fontFamily:T.font, marginTop:2 }}>ROAS {p.roas}x · CVR {p.cvr}%</div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <ChartCard title="Spend vs ROAS Matrix">
          <ResponsiveContainer width="100%" height={220}>
            <ScatterChart {...CHART_STYLE}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="adSpend" name="Ad Spend" tickFormatter={v=>`${(v/1000000).toFixed(1)}M`} tick={{fill:T.textDim,...CHART_STYLE}} label={{value:"Ad Spend",position:"insideBottom",offset:-5,fill:T.textDim,fontSize:10}}/>
              <YAxis dataKey="roas" name="ROAS" tickFormatter={v=>`${v}x`} tick={{fill:T.textDim,...CHART_STYLE}} label={{value:"ROAS",angle:-90,position:"insideLeft",fill:T.textDim,fontSize:10}}/>
              <Tooltip {...tooltipStyle} content={({payload})=>{
                if(!payload?.length) return null;
                const d = payload[0].payload;
                return <div style={{...tooltipStyle.contentStyle,padding:"10px"}}><div style={{color:T.text,fontSize:11}}>{d.name}</div><div style={{color:T.textMid,fontSize:10}}>Spend: {fmt.idr(d.adSpend)} | ROAS: {d.roas}x</div></div>;
              }}/>
              <ReferenceLine y={3} stroke={T.yellow} strokeDasharray="4 4"/>
              <Scatter data={productData} fill={T.blue}>
                {productData.map((p,i)=><Cell key={i} fill={scalingColors[categorize(p)]}/>)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Product Efficiency Score">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={[...productData].sort((a,b)=>b.efficiencyScore-a.efficiencyScore)} layout="vertical" {...CHART_STYLE}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis type="number" tick={{fill:T.textDim,...CHART_STYLE}}/>
              <YAxis type="category" dataKey="name" tick={{fill:T.textMid,...CHART_STYLE}} width={130} style={{fontSize:10}}/>
              <Tooltip {...tooltipStyle} formatter={v=>[v.toFixed(2),"Efficiency Score"]}/>
              <Bar dataKey="efficiencyScore" radius={[0,4,4,0]}>
                {[...productData].sort((a,b)=>b.efficiencyScore-a.efficiencyScore).map((p,i)=>(
                  <Cell key={i} fill={`hsl(${160-i*15},80%,55%)`}/>
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function SectionInsights(){
  const anomalies = productData.filter(p=>p.roas<2.5).slice(0,3);
  return (
    <div>
      <SectionHeader title="Agency Insight Layer" subtitle="Automated intelligence — anomalies, opportunities, and strategic signals for Growbiz analysts." badge="AI LAYER"/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:20 }}>
        <div style={{ background:T.surface, border:`1px solid ${T.red}40`, borderRadius:12, padding:16 }}>
          <div style={{ color:T.red, fontSize:11, fontFamily:T.font, letterSpacing:"0.08em", marginBottom:10 }}>⚠ ROAS ANOMALY ALERTS</div>
          {anomalies.map(p=>(
            <div key={p.id} style={{ background:T.redDim, borderRadius:6, padding:"8px 10px", marginBottom:8 }}>
              <div style={{ color:T.text, fontSize:11, fontFamily:T.font }}>{p.name}</div>
              <div style={{ color:T.red, fontSize:10, fontFamily:T.font, marginTop:2 }}>ROAS dropped to {p.roas}x — below break-even threshold</div>
            </div>
          ))}
        </div>
        <div style={{ background:T.surface, border:`1px solid ${T.accent}40`, borderRadius:12, padding:16 }}>
          <div style={{ color:T.accent, fontSize:11, fontFamily:T.font, letterSpacing:"0.08em", marginBottom:10 }}>🚀 SCALING OPPORTUNITIES</div>
          {productData.filter(p=>categorize(p)==="OPPORTUNITY").slice(0,3).map(p=>(
            <div key={p.id} style={{ background:T.accentDim, borderRadius:6, padding:"8px 10px", marginBottom:8 }}>
              <div style={{ color:T.text, fontSize:11, fontFamily:T.font }}>{p.name}</div>
              <div style={{ color:T.accent, fontSize:10, fontFamily:T.font, marginTop:2 }}>Strong ROAS {p.roas}x — fix CVR {p.cvr}% → unlock scale</div>
            </div>
          ))}
        </div>
        <div style={{ background:T.surface, border:`1px solid ${T.blue}40`, borderRadius:12, padding:16 }}>
          <div style={{ color:T.blue, fontSize:11, fontFamily:T.font, letterSpacing:"0.08em", marginBottom:10 }}>📊 CAMPAIGN HEALTH SCORE</div>
          {[
            { label:"Overall Ads Health", score:74, color:T.accent },
            { label:"Traffic Quality", score:61, color:T.blue },
            { label:"Conversion Efficiency", score:58, color:T.yellow },
            { label:"Budget Allocation", score:82, color:T.purple },
          ].map((h,i)=>(
            <div key={i} style={{ marginBottom:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                <span style={{ color:T.textMid, fontSize:10, fontFamily:T.font }}>{h.label}</span>
                <span style={{ color:h.color, fontSize:10, fontFamily:T.font, fontWeight:700 }}>{h.score}/100</span>
              </div>
              <div style={{ background:T.border, borderRadius:4, height:6 }}>
                <div style={{ width:`${h.score}%`, background:h.color, height:"100%", borderRadius:4 }}/>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <ChartCard title="Blended ROAS Trend">
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={storeData} {...CHART_STYLE}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="date" tick={{fill:T.textDim,...CHART_STYLE}} interval={6}/>
              <YAxis tick={{fill:T.textDim,...CHART_STYLE}} tickFormatter={v=>`${v}x`}/>
              <Tooltip {...tooltipStyle} formatter={v=>[`${v}x`,"Blended ROAS"]}/>
              <ReferenceLine y={4} stroke={T.accent} strokeDasharray="4 4" label={{value:"target",fill:T.accent,fontSize:9}}/>
              <Line type="monotone" dataKey="blendedROAS" stroke={T.purple} dot={false} strokeWidth={2.5}/>
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Strategic Recommendations">
          <div style={{ padding:"4px 0" }}>
            {[
              { icon:"↑", text:`Scale ${productData.filter(p=>categorize(p)==="SCALE").length} hero products — ROAS supports increased budget`, color:T.accent },
              { icon:"⚙", text:`${productData.filter(p=>categorize(p)==="OPPORTUNITY").length} products need CVR optimization before scaling`, color:T.blue },
              { icon:"🔻", text:`Ads contribution at ${adsContribAvg}% — monitor for organic health impact`, color:T.orange },
              { icon:"✗", text:`Pause ${productData.filter(p=>categorize(p)==="KILL").length} products dragging down portfolio ROAS`, color:T.red },
            ].map((r,i)=>(
              <div key={i} style={{ display:"flex", gap:10, padding:"8px 0", borderBottom:`1px solid ${T.border}40` }}>
                <span style={{ color:r.color, fontFamily:T.font, fontSize:14, width:20, flexShrink:0 }}>{r.icon}</span>
                <span style={{ color:T.textMid, fontFamily:T.font, fontSize:11, lineHeight:1.5 }}>{r.text}</span>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
    </div>
  );
}

// ─── SCHEMA SECTION ───────────────────────────────────────────────────────────
function SectionSchema(){
  const fields = [
    {name:"date",type:"DATE",desc:"Daily partition key"},
    {name:"product_id",type:"VARCHAR",desc:"Unique product identifier"},
    {name:"product_name",type:"VARCHAR",desc:"Product display name"},
    {name:"campaign_name",type:"VARCHAR",desc:"Shopee campaign label"},
    {name:"store_visitors",type:"INT",desc:"Total daily store traffic"},
    {name:"product_visitors",type:"INT",desc:"Product page visits"},
    {name:"impressions",type:"INT",desc:"Ad impression count"},
    {name:"clicks",type:"INT",desc:"Ad clicks"},
    {name:"orders",type:"INT",desc:"Total orders (store/product)"},
    {name:"product_sales",type:"BIGINT",desc:"Organic + ads product revenue"},
    {name:"ads_sales",type:"BIGINT",desc:"Revenue attributed to ads"},
    {name:"total_sales",type:"BIGINT",desc:"Store-level total GMV"},
    {name:"ad_spend",type:"BIGINT",desc:"Total advertising cost"},
  ];
  const derived = [
    {metric:"CTR",formula:"clicks / impressions"},
    {metric:"CVR (Ads)",formula:"orders / clicks"},
    {metric:"CVR (Store)",formula:"orders / store_visitors"},
    {metric:"AOV",formula:"total_sales / orders"},
    {metric:"ROAS",formula:"ads_sales / ad_spend"},
    {metric:"Organic Sales",formula:"total_sales − ads_sales"},
    {metric:"Ads Contribution %",formula:"ads_sales / total_sales"},
    {metric:"Blended ROAS",formula:"total_sales / ad_spend"},
    {metric:"Traffic Share %",formula:"product_visitors / SUM(product_visitors)"},
    {metric:"Sales Share %",formula:"product_sales / SUM(product_sales)"},
  ];
  return (
    <div>
      <SectionHeader title="Data Schema" subtitle="Recommended flat table structure for warehouse ingestion and dashboard computation." badge="DATA LAYER"/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <ChartCard title="Core Fields — shopee_daily_performance">
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11, fontFamily:T.font }}>
            <thead>
              <tr>
                {["Field","Type","Description"].map(h=><th key={h} style={{ padding:"6px 10px", textAlign:"left", color:T.textDim, borderBottom:`1px solid ${T.border}`, fontWeight:500 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {fields.map((f,i)=>(
                <tr key={i} style={{ borderBottom:`1px solid ${T.border}20` }}>
                  <td style={{ padding:"7px 10px", color:T.accent, fontFamily:"DM Mono, monospace" }}>{f.name}</td>
                  <td style={{ padding:"7px 10px", color:T.blue }}>{f.type}</td>
                  <td style={{ padding:"7px 10px", color:T.textMid }}>{f.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ChartCard>
        <ChartCard title="Derived Metrics — Computed at Query Time">
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11, fontFamily:T.font }}>
            <thead>
              <tr>
                {["Metric","Formula"].map(h=><th key={h} style={{ padding:"6px 10px", textAlign:"left", color:T.textDim, borderBottom:`1px solid ${T.border}`, fontWeight:500 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {derived.map((d,i)=>(
                <tr key={i} style={{ borderBottom:`1px solid ${T.border}20` }}>
                  <td style={{ padding:"7px 10px", color:T.yellow }}>{d.metric}</td>
                  <td style={{ padding:"7px 10px", color:T.textMid, fontFamily:"DM Mono, monospace", fontSize:10 }}>{d.formula}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ChartCard>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
const SECTIONS = [
  { id:"store", label:"Store Performance" },
  { id:"ads", label:"Ads Performance" },
  { id:"product_ads", label:"Product Ads" },
  { id:"product_detail", label:"Product Detail" },
  { id:"contribution", label:"Contribution" },
  { id:"impact", label:"Ads Impact" },
  { id:"scaling", label:"Scaling Framework" },
  { id:"insights", label:"Agency Insights" },
  { id:"schema", label:"Data Schema" },
];

export default function App(){
  const [active, setActive] = useState("store");

  const renderSection = () => {
    switch(active){
      case "store": return <Section1/>;
      case "ads": return <Section2/>;
      case "product_ads": return <Section3/>;
      case "product_detail": return <Section4/>;
      case "contribution": return <Section5/>;
      case "impact": return <Section6/>;
      case "scaling": return <SectionScaling/>;
      case "insights": return <SectionInsights/>;
      case "schema": return <SectionSchema/>;
      default: return null;
    }
  };

  return (
    <div style={{ background:T.bg, minHeight:"100vh", fontFamily:T.font }}>
      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&family=DM+Mono:wght@300;400;500&display=swap" rel="stylesheet"/>
      {/* Header */}
      <div style={{
        background:T.surface, borderBottom:`1px solid ${T.border}`,
        padding:"14px 32px", display:"flex", alignItems:"center", justifyContent:"space-between"
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{
            background:`linear-gradient(135deg, ${T.accent}, ${T.blue})`,
            width:32, height:32, borderRadius:8,
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:14, fontWeight:700, color:"#000"
          }}>G</div>
          <div>
            <div style={{ color:T.text, fontFamily:T.fontDisplay, fontWeight:700, fontSize:16 }}>Growbiz Intelligence</div>
            <div style={{ color:T.textDim, fontSize:10, fontFamily:T.font }}>Marketplace Growth Dashboard · Shopee</div>
          </div>
        </div>
        <div style={{ display:"flex", gap:16, alignItems:"center" }}>
          <div style={{ color:T.textDim, fontSize:10, fontFamily:T.font }}>Period: Apr 1–30, 2025</div>
          <div style={{ background:T.accentDim, color:T.accent, fontSize:10, padding:"4px 12px", borderRadius:20, fontFamily:T.font, border:`1px solid ${T.accentMid}` }}>● LIVE DEMO DATA</div>
        </div>
      </div>

      {/* Nav */}
      <div style={{
        background:T.surface, borderBottom:`1px solid ${T.border}`,
        padding:"0 32px", display:"flex", gap:0, overflowX:"auto",
      }}>
        {SECTIONS.map(s=>(
          <button key={s.id} onClick={()=>setActive(s.id)} style={{
            background:"transparent",
            border:"none", borderBottom:`2px solid ${active===s.id ? T.accent : "transparent"}`,
            color: active===s.id ? T.text : T.textDim,
            padding:"12px 16px", fontSize:11, fontFamily:T.font,
            cursor:"pointer", whiteSpace:"nowrap", transition:"all 0.2s",
          }}>{s.label}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding:"28px 32px", maxWidth:1400, margin:"0 auto" }}>
        {renderSection()}
      </div>

      {/* Footer */}
      <div style={{ borderTop:`1px solid ${T.border}`, padding:"14px 32px", display:"flex", justifyContent:"space-between" }}>
        <span style={{ color:T.textDim, fontSize:10, fontFamily:T.font }}>Growbiz Analytics Platform · Internal Use Only</span>
        <span style={{ color:T.textDim, fontSize:10, fontFamily:T.font }}>Data refreshes daily via Shopee Seller Center API</span>
      </div>
    </div>
  );
}
